// Pure layout for text with inline icons (spec §1): split into text / icon runs per line, wrap
// by words (a token belongs to the word it touches), and position runs for the given alignment.
// The style each text run (and the measuring) uses: the caller's style minus what IconText does
// itself for the whole block — wrapping (runs are pre-wrapped), the background box and its padding.
export function runStyle(style) {
  const { wordWrap, backgroundColor, padding, ...st } = style;
  return st;
}

const TOKEN = /\{([a-z_]+)\}/g;   // same as icons.mjs TOKEN_RE (kept local: this module stays registry-free)

function pieces(line, known) {
  const out = [];
  let last = 0;
  for (const m of line.matchAll(TOKEN)) {
    if (!known(m[1])) continue;
    if (m.index > last) out.push({ type: 'text', text: line.slice(last, m.index) });
    out.push({ type: 'icon', name: m[1] });
    last = m.index + m[0].length;
  }
  if (last < line.length) out.push({ type: 'text', text: line.slice(last) });
  return out;
}

// Words: split text runs after each space (the space stays on the left word). An icon never
// starts a word of its own: it joins the word before it, so "500 {coin}?" wraps as one unit
// and no line begins with a lonely icon. Only a leading icon ("{coin} 5") starts a word.
function words(ps) {
  const ws = [];
  let cur = [];
  const flush = () => { if (cur.length) ws.push(cur); cur = []; };
  for (const p of ps) {
    if (p.type === 'icon') {
      if (!cur.length && ws.length) cur = ws.pop();
      cur.push(p);
      continue;
    }
    for (const t of p.text.split(/(?<= )/)) { cur.push({ type: 'text', text: t }); if (t.endsWith(' ')) flush(); }
  }
  flush();
  return ws;
}

export function layoutIconText(str, { measure, fontSize, wrapWidth = 0, align = 'left', lineSpacing = 4, iconScale = 1.2, known = () => true }) {
  const iconW = Math.round(fontSize * iconScale);
  const widthOf = (p) => (p.type === 'icon' ? iconW : measure(p.text));
  const lines = [];
  for (const raw of String(str ?? '').split('\n')) {
    const ws = words(pieces(raw, known));
    let line = [], w = 0;
    const push = () => { lines.push(line); line = []; w = 0; };
    for (const word of ws) {
      const ww = word.reduce((s, p) => s + widthOf(p), 0);
      if (wrapWidth && line.length && w + ww > wrapWidth) push();
      line.push(...word); w += ww;
    }
    push();
  }
  const lineHeight = Math.max(fontSize, iconW) + lineSpacing;
  const merged = lines.map((ps) => {
    const runs = [];
    for (const p of ps) {                                 // join neighbouring text pieces
      const prev = runs[runs.length - 1];
      if (p.type === 'text' && prev?.type === 'text') prev.text += p.text; else runs.push({ ...p });
    }
    let x = 0;
    for (const r of runs) { r.w = widthOf(r); r.x = x; x += r.w; }
    return { runs, w: x };
  });
  const width = Math.max(0, ...merged.map((l) => l.w));
  merged.forEach((l, i) => {
    const dx = align === 'center' ? (width - l.w) / 2 : align === 'right' ? width - l.w : 0;
    for (const r of l.runs) r.x += dx;
    l.y = i * lineHeight;
  });
  return { lines: merged, width, height: merged.length * lineHeight - lineSpacing, lineHeight };
}

// Hit rectangle for an IconText (a Container): Phaser adds (width/2, height/2) to the local
// point, and our content spans -width*origin.x .. width*(1-origin.x).
export function hitArea(width, height, origin) {
  return { x: width * (0.5 - origin.x), y: height * (0.5 - origin.y), w: width, h: height };
}
